import { useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router';

type TScrollPosition = {
  element?: { x: number; y: number };
  window?: { x: number; y: number };
};

type TUseScrollRestoreOptions = {
  /** Whether to save/restore window scroll position (default: true) */
  saveWindowScroll?: boolean;
  /** Throttle delay for scroll events (ms) (default: 100) */
  throttleDelay?: number;
};

// Hook to restore scroll position on back navigation
export const useScrollRestore = (
  key: string,
  scrollRef?: React.RefObject<HTMLElement | null>,
  options: TUseScrollRestoreOptions = {},
) => {
  const { saveWindowScroll = true, throttleDelay = 100 } = options;
  const navType = useNavigationType();
  const positionRef = useRef<TScrollPosition>({});
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(null);

  // Restore on mount
  useEffect(() => {
    if (navType === 'POP') {
      const stored = sessionStorage.getItem(`scroll-position-${key}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as TScrollPosition;

          // Restore element scroll
          if (scrollRef?.current && parsed.element) {
            scrollRef.current.scrollTo(parsed.element.x, parsed.element.y);
          }

          // Restore window scroll
          if (saveWindowScroll && parsed.window) {
            window.scrollTo(parsed.window.x, parsed.window.y);
          }

          sessionStorage.removeItem(`scroll-position-${key}`);
        } catch (e) {
          console.warn('Failed to parse scroll position', e);
        }
      }
    }
  }, [navType, scrollRef, key, saveWindowScroll]);

  // Save on scroll
  useEffect(() => {
    const el = scrollRef?.current;

    const saveScroll = () => {
      if (throttleTimeoutRef.current) return;

      throttleTimeoutRef.current = setTimeout(() => {
        const scrollData: TScrollPosition = {
          element: el
            ? {
                x: el.scrollLeft,
                y: el.scrollTop,
              }
            : undefined,
          window: saveWindowScroll
            ? {
                x: window.scrollX,
                y: window.scrollY,
              }
            : undefined,
        };

        positionRef.current = scrollData;
        try {
          sessionStorage.setItem(`scroll-position-${key}`, JSON.stringify(scrollData));
        } catch (e) {
          console.warn('Failed to save scroll position', e);
        }

        throttleTimeoutRef.current = null;
      }, throttleDelay);
    };

    if (el) {
      el.addEventListener('scroll', saveScroll, { passive: true });
    }
    if (saveWindowScroll) {
      window.addEventListener('scroll', saveScroll, { passive: true });
    }

    return () => {
      if (el) el.removeEventListener('scroll', saveScroll);
      if (saveWindowScroll) window.removeEventListener('scroll', saveScroll);
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [scrollRef, key, saveWindowScroll, throttleDelay]);
};
